import aa from 'search-insights';
import {AlgoliaClient} from '@/api/algoliaclient';

const appID = process.env.VUE_APP_ALGOLIA_APP_ID
const browseGoalsIndex = process.env.VUE_APP_ALGOLIA_GOALS_INDEX

let browseGoalsClient;

async function setBrowseGoalsClient() {
  browseGoalsClient = await AlgoliaClient.create({
    appID,
    featureName: 'browse_goals',
    indices: browseGoalsIndex
  })
  // when running jest tests, NODE_ENV is set to 'test'
  if (process.env.NODE_ENV !== 'test') {
    await browseGoalsClient.initAnalytics()
  }
}

async function getBrowseGoalsClient() {
  if (!browseGoalsClient) {
    await setBrowseGoalsClient()
  }
  return browseGoalsClient
}


/*
  Performs the necessary multiple Algolia searches to get hits and enabled facets

  Returns/resolves to {
    hits: [...],
    nbHits,
    facets: {
      grades: [...],
      domains: [...],
      standards: [...]
    },
    // anything else that comes back with the results from the request with hits
  }
*/
export async function multiSearchAlgolia(browseFilters, standardSets, maxNumResults) {
  const query = browseFilters.keyword || ''
  const { hitsFacets, facetRequests } = requestsAndOptionsForBrowseFilters(browseFilters, standardSets)
  const client = await getBrowseGoalsClient()
  const indexName = client.getIndexName()
  const queries = [{
    indexName,
    params: {
      query,
      facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(browseFilters, standardSets),
      facets: hitsFacets,
      hitsPerPage: maxNumResults,
      responseFields: ['hits', 'facets', 'nbHits', 'processingTimeMS', 'query', 'params'],
      clickAnalytics: true
    }
  }]

  facetRequests.forEach(request => {
    let filters = {}
    request.filterFields.forEach(k => filters[k] = browseFilters[k])
    const shouldIncludeQuery = request.filterFields.includes('keyword')
    queries.push({
      indexName,
      params: {
        query: shouldIncludeQuery ? query : '',
        facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(filters, standardSets),
        facets: request.facets,
        responseFields: ['facets']
      }
    })
  })

  return client.multipleQueries(queries, { strategy: 'none' }).then(({ results }) => {
    let facets = {}
    const hitsResult = results[0]
    if (hitsResult.facets) {
      facets = Object.assign(facets, hitsResult.facets)
    }
    for (let i = 1; i < results.length; i++) {
      const response = results[i]
      if (response.facets) {
        facets = Object.assign(facets, response.facets)
      }
    }
    hitsResult.facets = facets
    return hitsResult
  })
}

export async function getGradesWithResultsForFilters(browseFilters, standardSets) {
  const client = await getBrowseGoalsClient()
  const resp = await client.search('', {
    facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(browseFilters, standardSets),
    facets: [FACET_GRADES],
    responseFields: ['nbHits', 'facets']
  })
  const gradeFacets = resp.facets?.grades || {}
  return Object.keys(gradeFacets)
}

export const FACET_GRADES = 'grades',
  FACET_DOMAINS = 'domains',
  FACET_STANDARDS = 'standard_set_refs.standard_long_ref',
  FACET_SUBJECT = 'subject',
  FACET_CHARACTERISTICS = 'characteristics'

/*
  Computes what facets and filter fields should be used for Algolia requests given what browse filters are set (encoded as a string). The implementation is basically a table/matrix. See https://docs.google.com/spreadsheets/d/1teWchROdyD_YsVrLTqBNQg5sJaf4MVtmK3lX9jrNvK8/edit#gid=0

  Returns {
    hitsFacets: [...],
    facetRequests: [
      {
        filterFields: [...],
        facets: [...]
      }
    ]
  }
*/
export function requestsAndOptionsForBrowseFilters(filters) {
  const encodedFilterCondition = encodeBrowseFilters(filters)
  // base requests per matrix
  let config
  switch (encodedFilterCondition) {
    case '10000':
      config = {
        hitsFacets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: []
      }
      break
    case '11000':
      config = {
        hitsFacets: [FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: [{
          facets: [FACET_GRADES],
          filterFields: ['subject'],
        }]
      }
      break
    case '11100':
      config = {
        hitsFacets: [FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'subjectDomain'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades'],
            facets: [FACET_DOMAINS]
          },
        ]
      }
      break
    case '11110':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'subjectDomain'],
            facets: [FACET_STANDARDS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'standard'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades', 'standard'],
            facets: [FACET_DOMAINS]
          },
        ]
      }
      break
    case '11101':
      config = {
        hitsFacets: [FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'keyword'],
            facets: [FACET_DOMAINS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
      break
    case '11111':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'subjectDomain', 'keyword'],
            facets: [FACET_STANDARDS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'standard', 'keyword'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades', 'standard', 'keyword'],
            facets: [FACET_DOMAINS]
          }
        ]
      }
      break
    case '11010':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades'],
            facets: [FACET_DOMAINS, FACET_STANDARDS]
          }
        ]
      }
      break
    case '11001':
      config = {
        hitsFacets: [FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
      break
    case '11011':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'keyword'],
            facets: [FACET_DOMAINS, FACET_STANDARDS]
          },
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
      break
    case '10100':
      config = {
        hitsFacets: [FACET_GRADES, FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_DOMAINS]
          }
        ]
      }
      break
    case '10110':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_DOMAINS]
          },
          {
            filterFields: ['subject', 'subjectDomain'],
            facets: [FACET_GRADES, FACET_STANDARDS]
          }
        ]
      }
      break
    case '10111':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'standard', 'keyword'],
            facets: [FACET_GRADES, FACET_DOMAINS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'keyword'],
            facets: [FACET_STANDARDS]
          }
        ]
      }
      break
    case '10101':
      config = {
        hitsFacets: [FACET_GRADES, FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_DOMAINS]
          }
        ]
      }
      break
    case '10010':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS]
          }
        ]
      }
      break
    case '10011':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS]
          }
        ]
      }
      break
    case '10001':
      config = {
        hitsFacets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: []
      }
      break
    case '00001':
      config = {
        hitsFacets: [FACET_GRADES],
        facetRequests: []
      }
      break
    case '01001':
      config = {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
      break
    default:
      console.warn('Unexpected filter condition ' + encodedFilterCondition)
      config = {
        hitsFacets: [],
        facetRequests: []
      }
      break
  }
  config = config || { hitsFacets: [], facetRequests: [] }
  addCharacteristicsRelatedFacetRequests(config, filters)
  return config
}

// Augment config with cross-facets for characteristics/subject relationship
// Used for enabling/disabling UI options without extra API calls
function addCharacteristicsRelatedFacetRequests(config, filters) {
  const hasCharacteristics = Array.isArray(filters.characteristics)
    ? filters.characteristics.length > 0
    : !!filters.characteristics

  if (hasCharacteristics) {
    // When characteristic is selected, get which subjects have results
    config.facetRequests.push({ filterFields: ['characteristics'], facets: [FACET_SUBJECT] })
  }
  if (filters.subject) {
    // When subject is selected, get which characteristics have results
    config.facetRequests.push({ filterFields: ['subject'], facets: [FACET_CHARACTERISTICS] })
  }
}

/*
This function makes the code for unit tests and computing facet requests for different combinations of filters present more manageable
 Encodes the browse filters into a binary string, where 1 indicates whether a filter is present and 0 if not
 Order of bits is: subject, grades, subjectDomain, standard, and keyword
 e.g. if only subject is set, we return 10000 (grades, subjectDomain, standard, and keyword are not set)
*/
export function encodeBrowseFilters(filters) {
  const subjectPresent = !!filters.subject,
    domainPresent = !!filters.subjectDomain,
    gradesPresent = filters.grades && filters.grades.length && true || false,
    queryPresent = !!filters.keyword,
    standardPresent = !!filters.standard

  return [subjectPresent, gradesPresent, domainPresent, standardPresent, queryPresent].map(b => b ? '1' : '0').join('')
}

/*
Generates the necessary facetFilters for an Algolia search request

filters structure:
  {
    grades: [],
    subject: '',
    subjectDomain: '',
    standard: '',
    keyword: '',
    levelOfSupport: 0,
    characteristics: []
  }
*/
export function generateAlgoliaFacetFiltersFromBrowseFilters(filters, standardSets) {
  const facetFilters = []
  if (filters.subject) {
    facetFilters.push([`subject:${filters.subject}`])
  }
  if (Array.isArray(filters.grades) && filters.grades.length) {
    const gradeFilters = []
    filters.grades.forEach(g => gradeFilters.push(`grades:${g}`))
    facetFilters.push(gradeFilters)
  }
  if (filters.subjectDomain) {
    facetFilters.push([`domains:${filters.subjectDomain}`])
  }
  if (filters.standard) {
    facetFilters.push([`standard_set_refs.standard_long_ref:${filters.standard}`])
  }
  if (Array.isArray(standardSets) && standardSets.length) {
    const standardFilters = standardSets.map(set => `standard_sets:${set}`)
    facetFilters.push(standardFilters)
  }
  if (filters.levelOfSupport >= 0) {
    facetFilters.push([`levels_of_support:${filters.levelOfSupport}`])
  }
  if (Array.isArray(filters.characteristics) && filters.characteristics.length) {
    const characteristicsFilters = []
    filters.characteristics.forEach(c => characteristicsFilters.push(`characteristics:${c}`))
    facetFilters.push(characteristicsFilters)
  }
  return facetFilters
}

/*
  Extracts facet values for grades, domains, and standards as arrays
*/
export function facetValuesWithResultsFromAlgoliaFacetResponse(facets) {
  return {
    grades: facets.grades && Object.keys(facets.grades) || null,
    domains: facets.domains && Object.keys(facets.domains) || null,
    standards: facets['standard_set_refs.standard_long_ref'] && Object.keys(facets['standard_set_refs.standard_long_ref']) || null
  }
}

/* Wrapper functions for sending algolia analytics events */
export async function sendAlgoliaClickedBrowseGoalResultEvent({userToken, queryID, objectIDs, positions}) {
  const client = await getBrowseGoalsClient()
  aa('clickedObjectIDsAfterSearch', {
    userToken: userToken || client.getPlaceholderUserToken(),
    eventName: 'Clicked Goal Tile',
    index: browseGoalsIndex,
    objectIDs,
    positions,
    queryID,
  })
}
